import { isSlotWatched, saveToLocalStorage, setSlotWatched } from './state.js';

interface SlotData {
  venue: string;
  date: string;
  court: string;
  time: string;
  status?: string;
  // Only a booked, still-upcoming slot can be newly watched — watching is
  // "tell me when this frees up", so a free or elapsed slot has nothing
  // to wait for. Unwatching is always allowed.
  watchable: boolean;
}

interface GridCell {
  td: HTMLElement;
  row: number;
  col: number;
  data: SlotData | null;
}

function readSlotData(td: HTMLElement): SlotData | null {
  const { venue, date, court, time, status, past } = td.dataset;
  if (!venue || !date || !court || !time) return null;
  return { venue, date, court, time, status, watchable: status === 'booked' && !past };
}

function cellDiv(td: HTMLElement): HTMLElement | null {
  return td.querySelector('.slot-cell');
}

// Touch has no "long press" the way this needs, so it's approximated: hold
// still this long before it counts as the start of a drag-selection rather
// than a scroll or a tap. Chosen short enough to feel responsive, long
// enough that a normal scroll-swipe (which moves within a few ms) never
// gets mistaken for it.
const TOUCH_LONG_PRESS_MS = 350;
// A finger moving more than this before the long-press timer fires means
// the gesture is a scroll, not a selection attempt — cancels the timer.
const TOUCH_MOVE_CANCEL_PX = 10;

// A real rectangle selection (like a spreadsheet), not a path the pointer
// happens to cross: every render tags each grid cell with its row/col
// (see render.ts), and on every move we recompute the full rectangle
// between the anchor and the currently-hovered cell — so a fast diagonal
// drag still selects the whole block between the two corners, not just
// whatever cells the path physically touched. On release, the gesture's
// target state (watch vs. unwatch) — decided once at the start from the
// anchor cell's own state — is applied to every real, watchable slot in
// the final rectangle. A plain click/tap with no movement degrades to just
// that one cell. `canSelect` is consulted before a gesture starts — watches
// are stored server-side per email, so nothing can be selected until one is
// set.
//
// Touch has no drag-while-button-down concept and sits on top of a
// scrollable table, so tap-vs-scroll-vs-select has to be disambiguated
// explicitly: a quick tap toggles one cell (mirroring a mouse click); a
// touch held still for TOUCH_LONG_PRESS_MS locks into the same rectangle
// gesture mouse dragging uses, blocking page/table scroll for the rest of
// that touch; any earlier movement past TOUCH_MOVE_CANCEL_PX is treated as
// an ordinary scroll and left alone.
export function initDragSelect(
  container: HTMLElement,
  onCommit: () => void,
  canSelect: () => boolean,
  onToggleReservation: (venue: string, date: string, court: string, time: string) => void
): void {
  let dragging = false;
  let anchor: { row: number; col: number } | null = null;
  let targetState = true;
  let grid: GridCell[] = [];
  let marked: GridCell[] = [];
  // row/col coordinates restart at 0 for each day's own table, so the grid
  // (and every cell hit-tested against it) must stay scoped to the one
  // table the drag started in — otherwise a rectangle would also light up
  // same-position cells in every other day's table.
  let activeTable: HTMLElement | null = null;

  function buildGrid(scope: HTMLElement): void {
    grid = Array.from(scope.querySelectorAll<HTMLElement>('[data-row][data-col]')).map((td) => ({
      td,
      row: Number(td.dataset.row),
      col: Number(td.dataset.col),
      data: readSlotData(td)
    }));
  }

  function clearPreview(): void {
    marked.forEach(({ td }) => cellDiv(td)?.classList.remove('selecting'));
    marked = [];
  }

  function applyRectangle(row: number, col: number): void {
    if (!anchor) return;
    clearPreview();
    const minRow = Math.min(anchor.row, row);
    const maxRow = Math.max(anchor.row, row);
    const minCol = Math.min(anchor.col, col);
    const maxCol = Math.max(anchor.col, col);

    marked = grid.filter((cell) => cell.row >= minRow && cell.row <= maxRow && cell.col >= minCol && cell.col <= maxCol);
    // Preview only what will actually change, so a free slot inside the
    // rectangle doesn't look selected and then silently stay unwatched.
    marked.forEach(({ td, data }) => {
      if (data && (!targetState || data.watchable)) cellDiv(td)?.classList.add('selecting');
    });
  }

  // Starts a gesture anchored at `td` — shared by mousedown, a completed
  // long-press, and a plain tap (which immediately follows with finish()).
  function beginSelection(td: HTMLElement, table: HTMLElement): void {
    activeTable = table;
    buildGrid(table);

    const row = Number(td.dataset.row);
    const col = Number(td.dataset.col);
    anchor = { row, col };

    const data = readSlotData(td);
    targetState = data ? !isSlotWatched(data.venue, data.date, data.court, data.time) : true;

    dragging = true;
    applyRectangle(row, col);
  }

  function finish(): void {
    if (!dragging) return;
    dragging = false;
    anchor = null;
    activeTable = null;

    // A touch long-press that never turned into a drag (still just its
    // single starting cell) means "mark/unmark as my own reservation"
    // instead of the usual watch toggle — see the touchstart long-press
    // timer below, which no longer gates on canSelect() up front so this
    // works with no email set (this feature is local-only, separate from
    // watching). touchLocked can only be true here on that exact gesture: a
    // plain tap never sets it, and a real drag ends up with more than one
    // marked cell. Anything except a closed slot can be marked — booked, or
    // still-available (reserved before the booking system caught up).
    if (touchLocked && marked.length === 1) {
      const { td, data } = marked[0];
      cellDiv(td)?.classList.remove('selecting');
      marked = [];
      if (data && data.status !== 'closed') {
        onToggleReservation(data.venue, data.date, data.court, data.time);
      }
      return;
    }

    // The mouse path already gated this at mousedown, before any dragging
    // ever started; touch's long-press timer (below) intentionally doesn't,
    // so a watch commit needs its own check here — reached only by touch.
    if (!canSelect()) {
      clearPreview();
      return;
    }

    let committedAny = false;
    marked.forEach(({ td, data }) => {
      cellDiv(td)?.classList.remove('selecting');
      if (!data) return;
      if (targetState && !data.watchable) return;
      setSlotWatched(data.venue, data.date, data.court, data.time, targetState);
      committedAny = true;
    });
    marked = [];

    if (committedAny) {
      saveToLocalStorage();
      onCommit();
    }
  }

  function abort(): void {
    clearPreview();
    dragging = false;
    anchor = null;
    activeTable = null;
  }

  function cellFromTarget(target: EventTarget | null): HTMLElement | null {
    return (target as HTMLElement | null)?.closest<HTMLElement>('[data-row][data-col]') ?? null;
  }

  // ---------- mouse ----------

  container.addEventListener('mousedown', (event) => {
    if (event.button !== 0) return;
    // The "my reservation" corner button handles its own click (see
    // main.ts) — not part of the watch drag-select at all.
    if ((event.target as HTMLElement).closest('.reservation-mark')) return;
    const td = cellFromTarget(event.target);
    if (!td) return;
    const table = td.closest<HTMLElement>('table.schedule-table');
    if (!table) return;
    if (!canSelect()) return;

    event.preventDefault(); // avoid native text-selection ghosting while dragging
    beginSelection(td, table);
  });

  // mouseover (not mouseenter) so a single listener on the container catches
  // pointer entry into any descendant cell, since mouseenter doesn't bubble.
  container.addEventListener('mouseover', (event) => {
    if (!dragging || !activeTable) return;
    const td = cellFromTarget(event.target);
    if (!td || !activeTable.contains(td)) return;
    applyRectangle(Number(td.dataset.row), Number(td.dataset.col));
  });

  window.addEventListener('mouseup', finish);
  window.addEventListener('blur', () => {
    abort();
  });

  // ---------- touch ----------

  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  let touchStart: { x: number; y: number } | null = null;
  let touchLocked = false; // true once the long-press has activated drag mode
  // True once the finger has moved past TOUCH_MOVE_CANCEL_PX without a long
  // press having locked in yet — a real scroll/swipe gesture, which must
  // also block the touchend tap-toggle fallback below, not just the timer.
  let touchMoved = false;

  function clearLongPress(): void {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function cellAtPoint(x: number, y: number): HTMLElement | null {
    return cellFromTarget(document.elementFromPoint(x, y));
  }

  function resetTouch(): void {
    clearLongPress();
    touchStart = null;
    touchLocked = false;
    touchMoved = false;
  }

  container.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length !== 1) return; // ignore pinch/multi-touch entirely
      const touch = event.touches[0];
      const td = cellFromTarget(touch.target);
      if (!td) return;
      const table = td.closest<HTMLElement>('table.schedule-table');
      if (!table) return;

      resetTouch();
      touchStart = { x: touch.clientX, y: touch.clientY };

      longPressTimer = setTimeout(() => {
        longPressTimer = null;
        // Deliberately no canSelect() gate here (unlike mouse's mousedown
        // handler) — a long-press that doesn't turn into a drag marks a
        // reservation instead of a watch (see finish()), which needs no
        // email at all. A long-press that *does* turn into a drag still
        // ends up gated, just later, at commit time in finish().
        touchLocked = true;
        beginSelection(td, table);
        navigator.vibrate?.(12); // best-effort tactile confirmation the drag started; no-op where unsupported
      }, TOUCH_LONG_PRESS_MS);
    },
    { passive: true }
  );

  container.addEventListener(
    'touchmove',
    (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];

      if (!touchLocked) {
        // Still waiting to see if this is a long-press: real scrolling
        // moves right away, so bail out and let the page/table scroll
        // normally instead of starting a selection later on release.
        if (touchStart && Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y) > TOUCH_MOVE_CANCEL_PX) {
          clearLongPress();
          touchMoved = true;
        }
        return;
      }

      event.preventDefault(); // locked into a selection: block scrolling for the rest of this touch
      const td = cellAtPoint(touch.clientX, touch.clientY);
      if (!td || !activeTable?.contains(td)) return;
      applyRectangle(Number(td.dataset.row), Number(td.dataset.col));
    },
    { passive: false }
  );

  container.addEventListener(
    'touchend',
    (event) => {
      if (touchLocked) {
        event.preventDefault(); // also suppresses the synthetic click this touch would otherwise fire
        finish();
      } else if (touchStart && !touchMoved) {
        // Released before the long-press fired, with no real movement in
        // between: a plain tap, toggling exactly the cell under the finger
        // — same rule as a no-movement mouse click. (A touch that moved but
        // never locked into a drag was a scroll/swipe, not a tap — see
        // touchMoved above — so it's left alone here.)
        clearLongPress();
        const touch = event.changedTouches[0];
        const td = cellAtPoint(touch.clientX, touch.clientY);
        const table = td?.closest<HTMLElement>('table.schedule-table');
        if (td && table) {
          // Suppress the synthetic mousedown/mouseup/click this touch would
          // otherwise fire — unconditionally, whether or not canSelect()
          // below actually does anything. Deferring this into the
          // canSelect()-gated branch let that synthetic click through
          // whenever no email was set yet: it fires after any DOM change
          // this tap made (e.g. the email dialog opening), gets hit-tested
          // fresh against the now-current DOM, lands on the dialog's
          // ::backdrop, and the dialog's own backdrop-click-closes handler
          // then immediately closed the dialog this same tap had just opened.
          event.preventDefault();
          if (canSelect()) {
            beginSelection(td, table);
            finish();
          }
        }
      }
      resetTouch();
    },
    { passive: false }
  );

  container.addEventListener('touchcancel', () => {
    abort();
    resetTouch();
  });
}
